import { can, type Me, type Permission } from '@taim/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { ApiRequestError, api } from './api';

export const meQueryKey = ['me'] as const;

export function useMeQuery() {
  return useQuery({
    queryKey: meQueryKey,
    queryFn: async () => {
      try {
        return await api.get<Me>('/api/me');
      } catch (e) {
        if (e instanceof ApiRequestError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}

type AuthValue = Me & { can: (p: Permission) => boolean };
const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ me, children }: { me: Me; children: ReactNode }) {
  // Stable while `me` is: every useAuth() consumer would otherwise re-render with this provider.
  const value = useMemo<AuthValue>(() => ({ ...me, can: (p) => can(me.user.role, p) }), [me]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** The signed-in user, tenant and permission check. Only usable inside the auth gate. */
export function useAuth(): AuthValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}

export function useSignIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { credential: string } | { email: string }) =>
      'credential' in input
        ? api.post<Me>('/api/auth/google', input)
        : api.post<Me>('/api/auth/test-login', input),
    onSuccess: (me) => qc.setQueryData(meQueryKey, me),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/api/auth/logout'),
    onSettled: () => {
      // Drop other users' cached data, but keep the `me` query (the auth gate observes it) and set it to null.
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== meQueryKey[0] });
      qc.setQueryData(meQueryKey, null);
    },
  });
}
